import AgencyMark from '../marks/AgencyMark'
import type { Mission } from '@/content/missions'
import { markStyleInfo, markStyles } from '@/content/marks'
import { displayFonts, sansFonts } from '@/content/fonts'
import type { EditOption } from '../inspector/Editable'
import type { Prefs } from '@/state/prefs'

// Option lists shared by the design notes and the "Every choice, live" tour.

/** Mark styles, each previewed with the active mission's own artwork. */
export function markOptionsFor(mission: Mission): Array<EditOption<Prefs['mark']>> {
  return markStyles.map((style) => ({
    value: style,
    label: markStyleInfo[style].name,
    preview: <AgencyMark mission={mission} variant={style} className="mds-mark-preview" />,
  }))
}

export const displayFontOptions: Array<EditOption<string>> = displayFonts.map((font) => ({
  value: font.id,
  label: font.name,
  preview: (
    <span className="mds-font-preview" style={{ fontFamily: font.stack }}>
      Aa
    </span>
  ),
}))

export const sansFontOptions: Array<EditOption<string>> = sansFonts.map((font) => ({
  value: font.id,
  label: font.name,
  preview: (
    <span className="mds-font-preview mds-font-preview--sans" style={{ fontFamily: font.stack }}>
      Aa
    </span>
  ),
}))

export const radiusOptions: Array<EditOption<Prefs['radius']>> = [
  { value: 'round', label: 'Round', preview: <span className="mds-shape-preview" style={{ borderRadius: 999 }} /> },
  { value: 'soft', label: 'Soft', preview: <span className="mds-shape-preview" style={{ borderRadius: 6 }} /> },
  { value: 'square', label: 'Square', preview: <span className="mds-shape-preview" style={{ borderRadius: 1 }} /> },
]

export const densityOptions: Array<EditOption<Prefs['density']>> = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
]

export const disclosureOptions: Array<EditOption<string>> = [
  { value: 'dialog', label: 'Info modal' },
  { value: 'expand', label: 'Expander' },
]

export const alignOptions: Array<EditOption<string>> = [
  { value: 'center', label: 'Centered' },
  { value: 'left', label: 'Left' },
]

export const sectionLinkOptions: Array<EditOption<Prefs['sectionLink']>> = [
  { value: 'outline', label: 'Outline' },
  { value: 'solid', label: 'Solid' },
  { value: 'text', label: 'Text' },
]

export const closingOptions: Array<EditOption<Prefs['closing']>> = [
  { value: 'gradient', label: 'Gradient' },
  { value: 'dark', label: 'Dark' },
  { value: 'plain', label: 'Plain' },
]

export const bannerToneOptions: Array<EditOption<string>> = [
  { value: 'theme', label: 'Theme' },
  { value: 'dark', label: 'Dark' },
  { value: 'primary', label: 'Primary' },
]

export const bannerScrollOptions: Array<EditOption<string>> = [
  { value: 'scroll', label: 'Hidden' },
  { value: 'sticky', label: 'Sticky' },
]

/** The banner's option groups after Disclosure, shared by its two notes. */
export function bannerGroups(prefs: Prefs, set: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void) {
  return [
    { label: 'Alignment', value: prefs.bannerAlign, options: alignOptions, onChange: (value: string) => set('bannerAlign', value as Prefs['bannerAlign']) },
    { label: 'Background', value: prefs.bannerTone, options: bannerToneOptions, onChange: (value: string) => set('bannerTone', value as Prefs['bannerTone']) },
    { label: 'On scroll', value: prefs.bannerScroll, options: bannerScrollOptions, onChange: (value: string) => set('bannerScroll', value as Prefs['bannerScroll']) },
  ]
}

export const alertOptions: Array<EditOption<Prefs['alert']>> = [
  { value: 'soft', label: 'Soft' },
  { value: 'solid', label: 'Solid' },
  { value: 'outline', label: 'Outline' },
  { value: 'bar', label: 'Side bar' },
]

export const accordionOptions: Array<EditOption<Prefs['accordion']>> = [
  { value: 'lines', label: 'Lines' },
  { value: 'cards', label: 'Cards' },
]

export const accordionIconOptions: Array<EditOption<string>> = [
  { value: 'plus', label: 'Plus' },
  { value: 'chevron', label: 'Chevron' },
]

export const textSizeOptions: Array<EditOption<string>> = [
  { value: 'small', label: 'Smaller' },
  { value: 'default', label: 'Default' },
  { value: 'large', label: 'Larger' },
]

/** The Size group shared by every Body font note. */
export function textSizeGroup(value: Prefs['textSize'], set: (value: Prefs['textSize']) => void) {
  return { label: 'Size', value, options: textSizeOptions, onChange: (next: string) => set(next as Prefs['textSize']) }
}

export const labelOptions: Array<EditOption<Prefs['label']>> = [
  { value: 'regular', label: 'Regular' },
  { value: 'bold', label: 'Bold' },
  { value: 'caps', label: 'Small caps' },
]

export const fieldOptions: Array<EditOption<Prefs['field']>> = [
  { value: 'outline', label: 'Outline' },
  { value: 'filled', label: 'Filled' },
  { value: 'underline', label: 'Underline' },
]

export const buttonOptions: Array<EditOption<Prefs['button']>> = [
  { value: 'medium', label: 'Medium' },
  { value: 'bold', label: 'Bold' },
  { value: 'caps', label: 'Caps' },
]

export const casingOptions: Array<EditOption<string>> = [
  { value: 'title', label: 'Title Case' },
  { value: 'sentence', label: 'Sentence case' },
]

export const brandOptions: Array<EditOption<string>> = [
  { value: 'word', label: 'Mission word' },
  { value: 'initials', label: 'Initials' },
  { value: 'name', label: 'Agency name' },
  { value: 'mds', label: 'Mission Design System' },
]

export const colorOptions: Array<EditOption<string>> = [
  { value: 'mission', label: 'Mission colors' },
  { value: 'custom', label: 'Custom' },
]

export const heroOptions: Array<EditOption<Prefs['hero']>> = [
  { value: 'photo', label: 'Photo' },
  { value: 'video', label: 'Video' },
  { value: 'flag', label: 'Waving flag' },
  { value: 'none', label: 'Plain' },
]

export const linkStyleOptions: Array<EditOption<Prefs['heroLinks']>> = [
  { value: 'text', label: 'Text' },
  { value: 'outline', label: 'Outline' },
  { value: 'solid', label: 'Solid' },
]

export const promptStyleOptions: Array<EditOption<string>> = [
  { value: 'raised', label: 'Raised' },
  { value: 'outline', label: 'Outline' },
  { value: 'glass', label: 'Glass' },
]

export const creditOptions: Array<EditOption<Prefs['credit']>> = [
  { value: 'caption', label: 'Caption' },
  { value: 'badge', label: 'Badge' },
]

export const eyebrowOptions: Array<EditOption<Prefs['eyebrow']>> = [
  { value: 'mono', label: 'Mono caps' },
  { value: 'sans', label: 'Sans caps' },
  { value: 'serif', label: 'Serif italic' },
  { value: 'accent', label: 'Accent' },
]

export const ctaOptions: Array<EditOption<Prefs['cta']>> = [
  { value: 'primary', label: 'Primary' },
  { value: 'accent', label: 'Accent' },
  { value: 'outline', label: 'Outline' },
]

export const footerOptions: Array<EditOption<Prefs['footer']>> = [
  { value: 'columns', label: 'Columns' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'dark', label: 'Dark' },
  { value: 'studio', label: 'Studio' },
]

export const identifierOptions: Array<EditOption<string>> = [
  { value: 'off', label: 'Off' },
  { value: 'on', label: 'On' },
]

export const footerRuleOptions: Array<EditOption<Prefs['footerRule']>> = [
  { value: 'line', label: 'Line' },
  { value: 'double', label: 'Double' },
  { value: 'stripes', label: 'Stripes' },
  { value: 'none', label: 'None' },
]

export const socialStyleOptions: Array<EditOption<Prefs['socialStyle']>> = [
  { value: 'icons', label: 'Icons' },
  { value: 'circles', label: 'Circles' },
  { value: 'none', label: 'None' },
]

export function fontDetail(id: string, list = displayFonts) {
  const font = list.find((item) => item.id === id)
  return font ? `${font.designer}. ${font.license ?? 'SIL Open Font License'}, Google Fonts.` : undefined
}

export const footerHeadingOptions: Array<EditOption<Prefs['footerHeading']>> = [
  { value: 'mono', label: 'Mono caps' },
  { value: 'sans', label: 'Sans bold' },
  { value: 'serif', label: 'Serif' },
  { value: 'small', label: 'Small sans' },
]

export const footerListOptions: Array<EditOption<Prefs['footerList']>> = [
  { value: 'plain', label: 'Plain' },
  { value: 'bullets', label: 'Bullets' },
  { value: 'arrows', label: 'Arrows' },
]
